import { useSelection } from "@/contexts/SelectionContext";
import type { LessonFields } from "@/types/Lesson";
import type { ProjectFields } from "@/types/Project";
import type { SlideFields } from "@/types/Slide";
import { IconButton } from "@ai-teach/ui-web";
import type React from "react";
import { FaUnlink } from "react-icons/fa";
import { MdCheckBoxOutlineBlank, MdOutlineDelete, MdRestore } from "react-icons/md";

const VARIANT_CLASSES = {
  default: "text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-secondary-200",
  danger: "text-error-500 hover:bg-error-500/10 hover:text-error-400",
  primary: "text-primary-500 hover:bg-primary-500/10 hover:text-primary-400",
} as const;

const POSITION_CLASSES = {
  top: "-top-10",
  bottom: "-bottom-10",
} as const;

const ALIGNMENT_CLASSES = {
  left: "left-0",
  right: "right-0",
  center: "left-1/2 -translate-x-1/2",
} as const;

interface Action {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  variant?: keyof typeof VARIANT_CLASSES;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

interface TrashCardActionBarProps {
  id: string;
  type: "project" | "lesson" | "slide";
  project?: ProjectFields;
  lesson?: LessonFields;
  slide?: SlideFields;
}

export function TrashCardActionBar({ id, type, project, lesson, slide }: TrashCardActionBarProps) {
  const {
    toggleProject,
    toggleLesson,
    toggleSlide,
    toggleDeleteModal,
    toggleIsSelectionMode,
    toggleRestoreModal,
    toggleUnlinkModalOpen,
  } = useSelection();

  const ACTIONS: Action[] = [
    {
      id: "select",
      icon: MdCheckBoxOutlineBlank,
      label: "Select",
      variant: "default",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          toggleProject(id, project.isLinkedCopy);
        } else if (type === "lesson" && lesson) {
          toggleLesson(id, lesson.isLinkedCopy);
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          toggleSlide(id, slide.isLinkedCopy);
        }

        toggleIsSelectionMode(true);
      },
    },
    {
      id: "restore",
      icon: MdRestore,
      label: "Restore",
      variant: "default",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          toggleProject(id, project.isLinkedCopy);
          toggleRestoreModal("Projects");
        } else if (type === "lesson" && lesson) {
          toggleLesson(id, lesson.isLinkedCopy);
          toggleRestoreModal("Lessons");
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          toggleSlide(id, slide.isLinkedCopy);
          toggleRestoreModal("Slides");
        }
      },
    },
    {
      id: "unlink",
      icon: FaUnlink,
      label: "Unlink",
      variant: "default",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          toggleUnlinkModalOpen({ type: "Project", itemId: project.id });
        } else if (type === "lesson" && lesson) {
          toggleUnlinkModalOpen({ type: "Lesson", itemId: lesson.id });
        } else if (type === "slide" && slide?.id) {
          toggleUnlinkModalOpen({ type: "Slide", itemId: slide.id });
        }
      },
    },
    {
      id: "delete",
      icon: MdOutlineDelete,
      label: "Delete",
      variant: "danger",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          toggleProject(id, project.isLinkedCopy);
        } else if (type === "lesson" && lesson) {
          toggleLesson(id, lesson.isLinkedCopy);
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          toggleSlide(id, slide.isLinkedCopy);
        }
        toggleDeleteModal(true);
      },
    },
  ];

  const isLinkedCopies =
    type === "slide"
      ? slide?.isLinkedCopy
      : type === "project"
        ? project?.isLinkedCopy
        : lesson?.isLinkedCopy;

  const cardActions = isLinkedCopies ? ACTIONS : ACTIONS.filter((a) => a.id !== "unlink");

  return (
    <>
      <div
        className={`absolute opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200
          bg-surface shadow-lg border border-gray-200 dark:border-gray-800 rounded-lg
          inline-flex items-center gap-0.5 px-1.5 py-1.5 z-50
          ${POSITION_CLASSES.top}
          ${ALIGNMENT_CLASSES.center}`}
        role="toolbar"
        aria-label="Card actions"
      >
        {cardActions.map((action) => (
          <div key={action.id} className="relative group/action">
            <IconButton
              onClick={action.onClick}
              variant={action.id === "delete" ? "danger" : "ghost"}
              size="sm"
              className={`${VARIANT_CLASSES[action.variant ?? "default"]} hover:scale-105`}
              aria-label={action.label}
              disabled={action.disabled}
            >
              <action.icon className="w-4 h-4" />
            </IconButton>
            <div
              className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 px-2 py-1
              bg-gray-900 text-white text-xs rounded-md whitespace-nowrap
              opacity-0 group-hover/action:opacity-100 transition-opacity
              pointer-events-none z-20 shadow-lg"
              role="tooltip"
            >
              {action.label}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
